




































import Vue from 'vue'
import Component from 'vue-class-component'
import {Prop, Watch} from 'vue-property-decorator'
import {Country, AdminDivision} from '@/modules/geo/types'
import {Action} from 'vuex-class'

/**
 * Componente para la selección de una división administrativa
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
@Component
export default class AdminDivisionSelector extends Vue {
    /**
     * Solicita a la aplicación una lista de divisiones administrativas según los
     * filtros, país y nivel de la división
     */
    @Action('geo/loadAdminDivisions')
    public loadAdminDivisions: (filters: { country: Country, level: number }) => AdminDivision[]

    /**
     * Define el país del cual se mostrarán las divisiones administrativas
     */
    @Prop({type: Country, default: null})
    public country: Country

    @Prop({type: [AdminDivision, Array], default: null})
    public value: AdminDivision | AdminDivision[]

    /**
     * Selección multiple
     *
     * Al activar esta opción el componente permitirá al usuario seleccionar
     * varías divisiones administrativas
     */
    @Prop({type: Boolean, default: false})
    public multiple: boolean

    /**
     * Muestra todas las divisiones administrativas del pais
     */
    @Prop({type: Boolean, default: false})
    public displayAll: boolean

    /**
     * Forzar mismo nivel
     *
     * Al activar esta opción el selector de niveles será deshabilitado cuando
     * el usuario seleccione la primera división administrativa
     */
    @Prop({type: Boolean, default: false})
    public forceSameLevel: boolean

    /**
     * Nivel de división administrativa seleccionado por el usuario
     */
    public currentLevel: number | any = null

    /**
     * Lista de divisiones administrativas cargadas según el nivel y pais
     * seleccionado por el usuario
     */
    public adminDivisions: AdminDivision[] = []


    /**
     * Observa la propiedad displayAll del componente y si esta es "true" cambiará
     * el nivel actual a 0.
     */
    @Watch('displayAll', {immediate: true})
    public watchDisplayAll (value: number) {
        value && (this.currentLevel = 0)
    }

    @Watch('multiple')
    public watchMultiple (value: boolean) {
        this.multiple ? (this.value = []) : (this.$emit('input', null))
    }

    /**
     * Observa la propiedad "country" que define el pais del cual se mostrarán
     * las divisiones. Cuando este cambia recarga la lista con las
     * div. administrativas correspondientes del nuevo
     * país.
     */
    @Watch('country', {immediate: true, deep: true})
    public async onCountryChange (value: Country) {
        if (!value || !value.id) {
            return
        }

        if (this.level === null) {
            return
        }

        this.adminDivisions = []

        await this.loadDivisions()
    }

    /**
     * Indica si el selector de niveles se encuentra deshabilitado
     */
    public get levelSelectorDisabled () {
        return this.noCountry
            || this.loading
            || this.forceSameLevel && this.multiple && (Array.isArray(this.value) ? !!this.value.length : !!this.value)
    }

    get level (): number | null {
        switch (true) {
            case this.displayAll:
                return 0;
            case this.forceSameLevel && Array.isArray(this.value) && !!this.value.length:
                return (this.value as AdminDivision[])[0].level
            case Array.isArray(this.value) && !!this.value.length:
                return 0;
            case this.value instanceof AdminDivision:
                return (this.value as AdminDivision).level
            default:
                return this.currentLevel
        }
    }

    public loading = false

    /**
     * Nombres de las divisiones administrativas según el pais seleccionado
     */
    public localizedDivisionNames: {[key: string]: string[]} = {
        es: [
            'Comunidades autónomas',
            'Provincias',
            'Municipios',
        ],
        de: [
            'Estados federados',
            'Regiones administrativas',
            'Distritos',
        ],
        it: [
            'Regiones',
            'Provincias',
            'Municipios',
        ],
        default: [
            'Nivel 1',
            'Nivel 2',
            'Nivel 3',
        ],
    }

    public get adminLevels () {
        const [level1, level2, level3] = (this.localizedDivisionNames[
            this.country && this.country.id ? this.country.code.toLowerCase() : 'default'
        ] || this.localizedDivisionNames['default'])

        return [
            {text: 'Todos', level: 0},
            {text: level1, level: 1},
            {text: level2, level: 2},
            {text: level3, level: 3},
        ]
    }

    get noCountry (): boolean {
        return !this.country || this.country.code === ''
    }

    /**
     * Cuando el nivel de divisiones cambia el componente solicita a la aplicación
     * una nueva lista con el nuevo nivel.
     */
    public async onLevelChange (level: any) {
        this.loading = true
        this.currentLevel = level
        // Carga la lista de divisiones administrativas
        await this.loadDivisions()
    }

    /**
     * Carga una lista de divisiones administrativas en función del pais y el
     * nivel seleccionado por el usuario
     */
    public async loadDivisions () {
        this.loading = true

        this.adminDivisions = await this.loadAdminDivisions({
            country: this.country,
            level: this.currentLevel,
        })

        this.loading = false
    }

    public errorCaptured () {
        this.loading = false
    }
}
